// Verbo animado del título del empty state ("Creá sin límites").
//
// Cicla una lista de verbos escribiéndolos y borrándolos letra por letra,
// tipo máquina de escribir: "Creá" → "Imaginá" → "Volá" → … El "sin límites"
// que va al lado NO se toca: sólo cambia el verbo.
//
// Detalles que importan:
//   • Un solo `setTimeout` por paso (nada de `setInterval`): cada fase tiene
//     su propio ritmo — escribir es más lento que borrar, y entre medio hay
//     pausas — y con un intervalo fijo habría que contar ticks a mano.
//   • `prefers-reduced-motion`: se muestra el primer verbo quieto. La animación
//     es decorativa; para quien pidió menos movimiento no aporta nada.
//   • El texto accesible es la frase completa y estable (`aria-label` en el
//     h1 de arriba): el lector de pantalla no debe deletrear el tipeo, así
//     que acá va todo `aria-hidden`.
import { useEffect, useState } from 'react';

// Todos tienen que leerse bien seguidos de "sin límites" y ser voseo rioplatense
// (el resto de la app tutea de vos: "Creá", "Describí lo que querés generar").
export const VERBOS = [
  'Creá',
  'Imaginá',
  'Volá',
  'Inventá',
  'Explorá',
  'Soñá',
  'Diseñá',
  'Producí',
] as const;

// Ritmos en ms. Borrar es ~la mitad que escribir porque al ojo le alcanza con
// ver que se deshace; si va al mismo ritmo, el ciclo se siente eterno.
const MS_ESCRIBIR = 75;
const MS_BORRAR = 38;
const MS_PAUSA_COMPLETO = 3900; // verbo entero en pantalla, antes de borrar
const MS_PAUSA_VACIO = 320; // vacío, antes de escribir el siguiente

type Fase = 'escribiendo' | 'completo' | 'borrando' | 'vacio';

export function VerboTipeado({
  className = '',
  // El Agente (chat-atlas) usa su propia lista de verbos con el mismo efecto
  // (ADR-2026-09-12-el-agente-toma-el-diseno-del-asistente-v2). Tiene que ser
  // estable entre renders: una lista nueva en cada render no rompe nada, pero
  // tampoco hace falta.
  verbos = VERBOS,
}: {
  className?: string;
  verbos?: readonly string[];
}) {
  const [indice, setIndice] = useState(0);
  const [largo, setLargo] = useState(verbos[0].length);
  const [fase, setFase] = useState<Fase>('completo');
  // Se lee UNA vez al montar: si alguien cambia la preferencia con el título en
  // pantalla, el próximo montaje lo toma. No vale la pena un listener acá.
  const [animar] = useState(
    () =>
      typeof window !== 'undefined' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (!animar) return;
    const verbo = verbos[indice % verbos.length];

    // Cada fase agenda la siguiente; el cleanup corta la cadena al desmontar.
    const [demora, avanzar]: [number, () => void] =
      fase === 'escribiendo'
        ? largo < verbo.length
          ? [MS_ESCRIBIR, () => setLargo((n) => n + 1)]
          : [0, () => setFase('completo')]
        : fase === 'completo'
          ? [MS_PAUSA_COMPLETO, () => setFase('borrando')]
          : fase === 'borrando'
            ? largo > 0
              ? [MS_BORRAR, () => setLargo((n) => n - 1)]
              : [0, () => setFase('vacio')]
            : [
                MS_PAUSA_VACIO,
                () => {
                  setIndice((i) => (i + 1) % verbos.length);
                  setFase('escribiendo');
                },
              ];

    const t = window.setTimeout(avanzar, demora);
    return () => window.clearTimeout(t);
  }, [animar, fase, largo, indice, verbos]);

  if (!animar) return <span className={className}>{verbos[0]}</span>;

  return (
    <span className={className} aria-hidden="true">
      {verbos[indice % verbos.length].slice(0, largo)}
      {/* Cursor: titila siempre (también mientras escribe y en las pausas, como
          el caret de un chat). El parpadeo es `.caret-titila` en index.css — a
          pasos, no un fade; ver el comentario de la keyframe. `inline-block` +
          altura en `em` para que escale con el tamaño del h1 sin tocar el
          interlineado.

          Color FIJO, no `bg-current`: el h1 va en gradiente con el texto en
          `transparent`, así que heredar el color dejaba el cursor invisible. */}
      <span
        className="inline-block w-[0.06em] h-[0.78em] translate-y-[0.06em] ml-[0.04em] bg-brand-primary caret-titila"
      />
    </span>
  );
}
